'use client'
import { useEffect, useMemo, useState } from 'react'
import { VStack } from '@chakra-ui/react'
import { type CreateInvoiceOrderDTO, Currency, type PickupDTO, type ShipmentDTO } from 'ecosystem'
import {
  carrierFieldsFromSelectedMethod,
  commaSeparator,
  convertMinorUnitToValue,
  createMinorUnit
} from 'shared-utils'
import { paymentShippingOptionsFactory } from 'api'
import { useUser } from 'auth'
import { overrideText } from 'ui/lib/overrides'
import { useOverridesContext } from 'ui/lib/overrides/hooks'
import { useUIGlobalsContext } from 'ui/lib/ui-globals/hooks'
import { useBuyerVat } from '../../../../common/hooks'
import { useCheckoutContext } from '../../../hooks'
import {
  BillingAddressPicker,
  DefaultPickupSelector,
  DeliveryMethodSelector,
  type DeliveryMethodOverrides,
  type DeliveryMode,
  pickupToOrderAddress,
  ShippingAddressPicker,
  ShippingMethodSelector,
  useBillingAddress,
  useShippingAddress
} from '../DefaultDelivery'
import { InvoiceReviewPanel } from './InvoiceReviewPanel'
import { useDebouncedDraftSync } from './useDebouncedDraftSync'

interface InvoiceDefaultProps {
  submitPath: string
}

/**
 * Invoice view for the DEFAULT shipping branch — no third-party carrier
 * widget runs, so the FE drives both the delivery-method choice
 * (delivery / pickup) and the shipping cost calc via
 * `paymentShippingFactory` over `shippingConfig.simpleShipping`.
 *
 * - Delivery → user-picked shipping address (company SHIPPING addresses
 *   with a "custom" fallback). `shippingCost` from the factory.
 * - Pickup → user-picked pickup location from `pickupOptions` (sourced
 *   from `store.pickups`). `shippingCost = 0`.
 *
 * If the store has no pickup options configured, the method switcher is
 * hidden and the flow stays in delivery mode.
 */
export const InvoiceDefault = ({ submitPath }: InvoiceDefaultProps) => {
  const { user } = useUser()
  const {
    pickupOptions,
    summaryTotal,
    netSummaryTotal,
    shippingConfig,
    invoiceOrderResponse,
    paymentProviderConfig
  } = useCheckoutContext()
  const buyerVat = useBuyerVat(paymentProviderConfig)
  const { priceMaximumFractionDigits } = useUIGlobalsContext()
  const overrides = useOverridesContext<keyof DeliveryMethodOverrides>()
  const freeShippingLabel = overrideText('Fri frakt', overrides?.invoiceDeliveryFreeLabel)
  const { billingAddress, setBillingAddress } = useBillingAddress(
    invoiceOrderResponse?.storefrontOrderResponse?.billingAddress
  )

  const currency = invoiceOrderResponse?.storefrontOrderResponse?.currency ?? Currency.SEK
  const formatCost = (n: number) => `${commaSeparator(n, priceMaximumFractionDigits)} ${currency}`

  const pickups = pickupOptions ?? []
  const pickupAvailable = pickups.length > 0

  const configuredMethods = shippingConfig?.defaultOptions?.methods
  const hasConfiguredMethods = Boolean(configuredMethods?.length)

  const [mode, setMode] = useState<DeliveryMode>('DELIVERY')
  const [methodId, setMethodId] = useState<string | undefined>(undefined)

  const { shippingAddress, setShippingAddress } = useShippingAddress()

  const [selectedPickup, setSelectedPickup] = useState<PickupDTO | undefined>(undefined)

  // Cart-loaded gate. `summaryTotal` is 0 on the first client render before
  // Redux hydrates the cart, and `paymentShippingFactory(0, …)` falls under
  // any free-shipping threshold and returns the paid `deliveryFee`. Without
  // this guard the initial render would publish a bogus shipping cost to the
  // draft DTO, then get overwritten once the cart actually loads — racy if
  // the debounce window closes between the two renders.
  const isCartReady = summaryTotal > 0

  // Every option this buyer can pick, priced in minor units. The invoice flow
  // owns the shipping address outright, so country-conditioned options resolve
  // properly here — unlike the widget flows, which learn the address later.
  const shippingOptions = useMemo(() => {
    if (!isCartReady) return []
    return paymentShippingOptionsFactory({
      orderAmount: createMinorUnit(summaryTotal),
      // Rates the merchant configured ex-VAT are evaluated against this — the
      // basis a B2B buyer's thresholds are normally negotiated on.
      netOrderAmount: createMinorUnit(netSummaryTotal),
      locale: 'sv',
      opts: shippingConfig?.simpleShipping,
      shippingConfig,
      recipientCountryCode: shippingAddress?.country,
      // The configured fee is VAT-inclusive at the store's rate; a zero-rated
      // buyer owes no VAT on freight either.
      buyerVat,
      customerType: user?.customer?.type,
      pickupAvailable
    })
  }, [
    buyerVat,
    isCartReady,
    pickupAvailable,
    shippingAddress?.country,
    shippingConfig,
    summaryTotal,
    netSummaryTotal,
    user?.customer?.type
  ])

  const selectedOption = useMemo(
    () =>
      shippingOptions.find((option) => option.id === methodId) ??
      shippingOptions.find((option) => option.preselected) ??
      shippingOptions[0],
    [methodId, shippingOptions]
  )

  // A rule change can retire the selected option; reselect so the draft never
  // carries a price for something no longer on offer.
  useEffect(() => {
    if (selectedOption && selectedOption.id !== methodId) {
      setMethodId(selectedOption.id)
    }
  }, [methodId, selectedOption])

  const deliveryShippingCost = convertMinorUnitToValue(
    shippingOptions.find((option) => option.kind !== 'PICKUP')?.price ?? 0
  )

  const effectiveMode: DeliveryMode = hasConfiguredMethods
    ? selectedOption?.kind === 'PICKUP'
      ? 'PICKUP'
      : 'DELIVERY'
    : mode

  const shippingCost = hasConfiguredMethods
    ? convertMinorUnitToValue(selectedOption?.price ?? 0)
    : effectiveMode === 'PICKUP'
      ? 0
      : deliveryShippingCost

  // A pickup option may restrict which of the store's locations it offers.
  const availablePickups = useMemo(() => {
    const allowedIds = selectedOption?.pickupIds
    if (!allowedIds?.length) return pickups

    const matched = pickups.filter((pickup) => allowedIds.includes(pickup.id))
    // A restriction that matches nothing is a stale or mistyped id, not an
    // instruction to offer no pickup at all — that would leave the shopper on
    // a pickup option they can never complete. Fall back to every location, the
    // same way `resolveDefaultShippingMethods` falls back when a rule hides
    // every delivery option.
    return matched.length ? matched : pickups
  }, [pickups, selectedOption?.pickupIds])

  // Preselect as soon as pickup is chosen — the store's default location, else
  // the first offered — and reselect when a method change retires it.
  useEffect(() => {
    if (effectiveMode !== 'PICKUP') return
    if (selectedPickup && availablePickups.some((pickup) => pickup.id === selectedPickup.id)) return

    const next = availablePickups.find((pickup) => pickup.defaultPickup) ?? availablePickups[0]
    if (next) setSelectedPickup(next)
  }, [availablePickups, effectiveMode, selectedPickup])

  // `shippingProvider` is intentionally omitted — the DEFAULT branch has no
  // third-party carrier and the BE's `ShippingProvider` enum only accepts
  // the three real carriers (POSTNORD / NSHIFT / INGRID).
  const shipment: ShipmentDTO = useMemo(() => {
    // The chosen option is mirrored onto the carrier fields so fulfilment sees
    // the service without reading `additionalInfo`.
    const service = carrierFieldsFromSelectedMethod(
      hasConfiguredMethods ? selectedOption : undefined
    )

    if (effectiveMode === 'PICKUP' && selectedPickup) {
      return {
        shippingType: 'PICKUP',
        locationId: selectedPickup.id,
        locationName: selectedPickup.name,
        address: pickupToOrderAddress(selectedPickup),
        ...service
      }
    }
    return {
      shippingType: 'DELIVERY',
      ...(shippingAddress ? { address: shippingAddress } : {}),
      ...service
    }
  }, [effectiveMode, hasConfiguredMethods, selectedOption, selectedPickup, shippingAddress])

  const draftOverrides: Partial<CreateInvoiceOrderDTO> = {
    shippingCost,
    shipments: [shipment],
    ...(billingAddress ? { billingAddress } : {})
  }

  const isValid =
    (effectiveMode === 'DELIVERY' && !!shippingAddress) ||
    (effectiveMode === 'PICKUP' && !!selectedPickup)

  const { flush: flushDraftSync } = useDebouncedDraftSync(draftOverrides, isValid && isCartReady)

  return (
    <VStack align="stretch" gap={6} w="100%">
      {hasConfiguredMethods ? (
        <ShippingMethodSelector
          formatCost={formatCost}
          freeLabel={freeShippingLabel}
          onChange={setMethodId}
          options={shippingOptions}
          selectedId={selectedOption?.id}
        />
      ) : (
        <DeliveryMethodSelector
          deliveryCost={deliveryShippingCost}
          formatCost={formatCost}
          onChange={setMode}
          pickupAvailable={pickupAvailable}
          value={mode}
        />
      )}

      {effectiveMode === 'DELIVERY' ? (
        <ShippingAddressPicker onChange={setShippingAddress} value={shippingAddress} />
      ) : (
        <DefaultPickupSelector
          onChange={setSelectedPickup}
          pickups={availablePickups}
          selectedId={selectedPickup?.id}
        />
      )}

      <BillingAddressPicker onChange={setBillingAddress} value={billingAddress} />

      <InvoiceReviewPanel
        draftOverrides={draftOverrides}
        flushDraftSync={flushDraftSync}
        isDisabled={!isValid}
        submitPath={submitPath}
      />
    </VStack>
  )
}
